import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Clock,
  DollarSign,
  CheckCircle,
  RefreshCw,
  Bell,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface PendingApproval {
  id: string;
  userId: string;
  operationType: string;
  amount: number;
  requestedAt: string;
  expiresAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

interface CommissionSummary {
  total: number;
  pending: number;
  paid: number;
  byOperation: Record<string, number>;
}

export function ManagerDashboard() {
  const [period, setPeriod] = useState('30d');

  const { data: pendingApprovals, isLoading: approvalsLoading, refetch: refetchApprovals } = useQuery({
    queryKey: ['manager-pending-approvals'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.approvals.pending);
      return response.data as PendingApproval[];
    },
  });

  const { data: commissions, isLoading: commissionsLoading } = useQuery({
    queryKey: ['manager-commissions-summary', period],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.managerCommissions.summary, { params: { period } });
      return response.data as CommissionSummary;
    },
  });

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['manager-users'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.managerUsers.list);
      return response.data as { users: any[]; total: number };
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const hoursUntilExpiry = (expires.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilExpiry < 4;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Painel do Gerente</h1>
          <p className="text-surface-400">Gerencie usuários, aprovações e acompanhe suas comissões</p>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '7d', label: 'Últimos 7 dias' },
              { value: '30d', label: 'Últimos 30 dias' },
              { value: '90d', label: 'Últimos 90 dias' },
            ]}
          />
          <Button variant="secondary" onClick={() => refetchApprovals()} disabled={approvalsLoading}>
            <RefreshCw className={`w-4 h-4 ${approvalsLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Usuários</p>
                {usersLoading ? (
                  <div className="h-8 w-16 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {users?.total || 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Aprovações Pendentes</p>
                {approvalsLoading ? (
                  <div className="h-8 w-16 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {pendingApprovals?.length || 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Comissões Pendentes</p>
                {commissionsLoading ? (
                  <div className="h-8 w-24 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(commissions?.pending || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Total Comissões</p>
                {commissionsLoading ? (
                  <div className="h-8 w-24 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(commissions?.total || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Pending Approvals */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-yellow-400" />
            Aprovações Pendentes
            {pendingApprovals && pendingApprovals.length > 0 && (
              <Badge variant="warning">{pendingApprovals.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {approvalsLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : !pendingApprovals || pendingApprovals.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma aprovação pendente</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {pendingApprovals.map(approval => (
                <div
                  key={approval.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isExpiringSoon(approval.expiresAt) ? 'bg-red-500/20' : 'bg-yellow-500/20'
                    }`}>
                      <Clock className={`w-6 h-6 ${
                        isExpiringSoon(approval.expiresAt) ? 'text-red-400' : 'text-yellow-400'
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium text-surface-100">{approval.user.name}</p>
                      <p className="text-sm text-surface-500">
                        {approval.operationType} • {formatCurrency(approval.amount)}
                      </p>
                      <p className="text-xs text-surface-500">
                        Solicitado em {formatDate(approval.requestedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {isExpiringSoon(approval.expiresAt) && (
                      <Badge variant="error">Expira em breve</Badge>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => window.location.href = `/manager/approvals?id=${approval.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Revisar
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Commissions by Operation */}
      {commissions?.byOperation && Object.keys(commissions.byOperation).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Comissões por Operação
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-surface-800">
              {Object.entries(commissions.byOperation).map(([operation, amount]) => (
                <div
                  key={operation}
                  className="flex items-center justify-between px-6 py-4"
                >
                  <span className="text-surface-300">{operation}</span>
                  <span className="text-green-400 font-medium">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </motion.div>
  );
}
